<script setup>
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Logo from 'next/icon/Logo.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle, isOpen }">
      <!-- Collapsed view: Logo trigger -->
      <button
        v-if="isCollapsed"
        class="grid flex-shrink-0 place-content-center size-10 rounded-xl cursor-pointer hover:bg-accent transition-all duration-200 shadow-sm border border-border/50 bg-primary/30"
        :class="{ 'bg-accent ring-2 ring-primary/20': isOpen }"
        :title="currentAccount.name"
        @click="toggle"
      >
        <Logo class="size-6 text-primary" />
      </button>
      <!-- Expanded view: Account name trigger -->
      <button
        v-else
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-xl px-3 py-2 transition-all duration-200 group"
        :class="[
          isOpen
            ? 'bg-accent ring-1 ring-border shadow-inner'
            : 'hover:bg-accent/50',
          showAccountSwitcher ? 'cursor-pointer' : 'cursor-default',
        ]"
        @click="() => showAccountSwitcher && toggle()"
      >
        <div class="flex flex-col items-start min-w-0">
          <span
            class="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest leading-none mb-1"
          >
            {{ t('SIDEBAR_ITEMS.SWITCH_ACCOUNT') }}
          </span>
          <span
            class="text-sm font-bold leading-none text-foreground truncate max-w-[140px]"
            aria-live="polite"
          >
            {{ currentAccount.name }}
          </span>
        </div>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevrons-up-down size-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody
      v-if="showAccountSwitcher || isCollapsed"
      class="min-w-[280px] z-50 p-2 rounded-2xl bg-background/95 backdrop-blur-xl border border-border shadow-2xl transition-all"
    >
      <DropdownSection
        :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
        class="px-2 pb-2"
      >
        <DropdownItem
          v-for="account in sortedCurrentUserAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          class="cursor-pointer rounded-xl px-3 py-2.5 mb-1 hover:bg-accent group transition-all"
          @click="onChangeAccount(account.id)"
        >
          <template #label>
            <div
              :for="account.name"
              class="text-left rtl:text-right flex gap-3 items-center flex-grow min-w-0"
            >
              <div
                class="size-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors"
              >
                <span class="text-primary font-bold text-xs">{{
                  account.name.charAt(0).toUpperCase()
                }}</span>
              </div>
              <div class="flex flex-col min-w-0 flex-grow">
                <span
                  class="text-sm font-semibold text-foreground truncate min-w-0"
                  :title="account.name"
                >
                  {{ account.name }}
                </span>
                <span
                  class="text-[10px] text-muted-foreground font-medium uppercase tracking-wider truncate"
                  :title="account.name"
                >
                  {{
                    account.custom_role_id
                      ? account.custom_role.name
                      : account.role
                  }}
                </span>
              </div>
            </div>
            <span
              v-show="account.id === accountId"
              class="i-lucide-check size-4 text-primary ml-2 flex-shrink-0"
            />
          </template>
        </DropdownItem>
      </DropdownSection>
      <div class="px-2 pt-2 border-t border-border/50">
        <DropdownItem
          v-if="globalConfig.createNewAccountFromDashboard"
          class="p-0"
        >
          <ButtonNext
            variant="faded"
            class="w-full rounded-xl bg-accent/50 hover:bg-accent border-none font-bold text-xs h-10"
            @click="emitNewAccount"
          >
            <template #icon>
              <span class="i-lucide-plus-circle size-4 mr-2" />
            </template>
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </ButtonNext>
        </DropdownItem>
      </div>
    </DropdownBody>
  </DropdownContainer>
</template>
